import { ENDPOINTS } from '../utils/endpoints';
import {
  getAPIwithToken,
  postAPIwithToken,
  patchAPIwithToken,
  putAPIwithToken,
  deleteAPIwithToken,
} from '../utils/fetchApi';
import type {
  Role,
  Permission,
  RbacUser,
  UserProfileResponse,
  MenuItem,
} from '../types/rbac';

export interface ApiResponse<T = any> {
  status?: string;
  message?: string;
  data: T;
  total?: number;
  page?: number;
  limit?: number;
}

// 1. User Profile & Dynamic Navigation
export async function getUserProfile(): Promise<ApiResponse<UserProfileResponse>> {
  return getAPIwithToken<ApiResponse<UserProfileResponse>>(ENDPOINTS.PROFILE);
}

export async function getUserMenu(): Promise<ApiResponse<MenuItem[]>> {
  return getAPIwithToken<ApiResponse<MenuItem[]>>(ENDPOINTS.USER_MENU);
}

// 2. Role Management
export async function getRolesList(): Promise<ApiResponse<Role[]>> {
  return getAPIwithToken<ApiResponse<Role[]>>(ENDPOINTS.ROLES);
}

export async function getRoleDetail(id: number): Promise<ApiResponse<Role>> {
  return postAPIwithToken<ApiResponse<Role>>(ENDPOINTS.ROLE_DETAIL, { id });
}

export async function createRole(payload: {
  name: string;
  description?: string;
}): Promise<ApiResponse<Role>> {
  return postAPIwithToken<ApiResponse<Role>>(ENDPOINTS.ROLES, {
    name: payload.name.toUpperCase(),
    description: payload.description,
  });
}

export async function updateRole(payload: {
  id: number;
  name?: string;
  description?: string;
}): Promise<ApiResponse<Role>> {
  return patchAPIwithToken<ApiResponse<Role>>(ENDPOINTS.ROLES, payload);
}

export async function updateRolePermissions(payload: {
  id: number;
  permissionIds: number[];
}): Promise<ApiResponse<any>> {
  return putAPIwithToken<ApiResponse<any>>(ENDPOINTS.ROLE_PERMISSIONS, payload);
}

// 3. Permission Management
export async function getPermissionsList(): Promise<ApiResponse<Permission[]>> {
  return getAPIwithToken<ApiResponse<Permission[]>>(ENDPOINTS.PERMISSIONS);
}

export async function createPermission(payload: {
  permissionKey: string;
  description?: string;
}): Promise<ApiResponse<Permission>> {
  return postAPIwithToken<ApiResponse<Permission>>(ENDPOINTS.PERMISSIONS, payload);
}

export async function updatePermission(payload: {
  id: number;
  permissionKey?: string;
  description?: string;
}): Promise<ApiResponse<Permission>> {
  return patchAPIwithToken<ApiResponse<Permission>>(ENDPOINTS.PERMISSIONS, payload);
}

export async function deletePermission(id: number): Promise<ApiResponse<any>> {
  return deleteAPIwithToken<ApiResponse<any>>(ENDPOINTS.PERMISSIONS, { id });
}

// 4. User Multi-Role Management
export async function getPaginatedUsers(payload: {
  page: number;
  limit: number;
  search?: string;
  role?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'Active' | 'Inactive';
}): Promise<ApiResponse<{ users: RbacUser[]; total: number; page: number; limit: number }>> {
  return postAPIwithToken<ApiResponse<{ users: RbacUser[]; total: number; page: number; limit: number }>>(
    ENDPOINTS.USERS,
    payload
  );
}

export async function createMultiRoleUser(payload: {
  name: string;
  userId: string;
  email: string;
  phone?: string;
  password?: string;
  roleIds: number[];
  scopes?: any[];
}): Promise<ApiResponse<RbacUser>> {
  return postAPIwithToken<ApiResponse<RbacUser>>(ENDPOINTS.CREATE_USER, payload);
}

export async function updateMultiRoleUser(payload: {
  id: number;
  name?: string;
  roleIds?: number[];
  status?: 'ACTIVE' | 'INACTIVE' | 'Active' | 'Inactive';
}): Promise<ApiResponse<RbacUser>> {
  return patchAPIwithToken<ApiResponse<RbacUser>>(ENDPOINTS.EDIT_USER, payload);
}

export async function quickAssignUserRoles(payload: {
  userId: number;
  roleIds: number[];
}): Promise<ApiResponse<any>> {
  return putAPIwithToken<ApiResponse<any>>(ENDPOINTS.USER_ROLES, payload);
}
